import axios, { CanceledError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import {
  clearActingClientStorage,
  isActingClientRefusal,
  signalActingClientRefused,
} from 'src/sections/customer/acting-client/acting-client-storage';
// config
import { HOST_API_LEGACY, HOST_API, FIREBASE_API } from 'src/config-global';
import { paths } from 'src/routes/paths';
import { getAuth, onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
// utils
import {
  localStorageGetItem,
  localStorageSetItem,
  localStorageRemoveItem,
  AUTH_STORAGE_KEYS,
} from './storage-available';

// ----------------------------------------------------------------------

export const endpoints = {
  // NAT-2788 — the Natives Coffee dashboard, one snapshot of every reading.
  coffee: {
    report: '/admin/coffee-stats/report',
  },
  auth: {
    login: '/auth/login',
    refreshToken: '/auth/refresh-token',
    passwords: '/auth/passwords',
    firebase: {
      loginWithPassword: '/auth/login/new',
    },
  },
  admin: {
    tableText: '/admin/texts',
    tableTextByKey: (key: string) => `/admin/text/${key}`,
    producer_dues: '/admin/dashboard/producer-dues',
    assistantMetrics: '/admin/assistant/metrics',
    assistantConversations: '/admin/assistant/conversations',
    assistantConversation: (id: string) => `/admin/assistant/conversations/${id}`,
    assistantUnserved: '/admin/assistant/unserved',
    // NAT-2524 — the reorder sheet over v_product_reorder_analysis, grouped by producer.
    reorderSheet: '/admin/reorder-sheet',
    // NAT-2654 — archive holds: the list, and one product's hold. Admin only.
    archiveHolds: {
      root: '/admin/archive-holds',
      product: (sku: string) => `/admin/products/${encodeURIComponent(sku)}/archive-hold`,
      release: (sku: string) => `/admin/products/${encodeURIComponent(sku)}/archive-hold/release`,
      allocate: (sku: string) => `/admin/products/${encodeURIComponent(sku)}/archive-hold/allocate`,
    },
    clients: '/admin/clients',
    // NAT-2499 (phase 6) — venues ever associated with the same company, for the admin
    // link-management form's suggestion banner. A suggestion only; never a grant, and not
    // a claim of current ownership (round 3 of the review).
    clientCompanyVenues: (clientId: string | number) => `/admin/clients/${clientId}/company-venues`,
    companies: '/admin/companies',
    producerFicEntities: '/admin/producer-fic-entities',
    producerCompanies: '/admin/producer-companies',
    clientsExcel: '/admin/clients-excel',
    agentsStat: '/admin/agents_stat',
    agents: '/admin/agents',
    agentServiceZone: '/admin/agents/agent-service-area',
    clientRegions: '/admin/client_regions',
    clientRegionsProvinces: '/admin/client_all_region_provinces',
    countries: '/admin/countries',
    agent_associated_clients: '/admin/agent/associated-clients',
    client_discount: 'stats/discount',
    overlay: {
      root: '/admin/overlay',
      activePDF: '/admin/overlay/active/pdf',
    },
    targets: '/admin/targets',
    logisticaIt: {
      syncProducers: '/admin/logistica-it/sync-producers',
      syncProducts: '/admin/logistica-it/sync-products',
      syncClients: '/admin/logistica-it/sync-clients',
      syncStock: '/admin/logistica-it/sync-stock',
    },
    ficSync: {
      syncVat: '/admin/clients/sync-fic-vat',
    },
    fic_suppliers: '/admin/fic-suppliers',
    // NAT-1713 — collection tracking behind "Da incassare". Not under /admin/dashboard: these
    // are writes against a client, not dashboard reads.
    collectionActivities: {
      root: '/admin/collection-activities',
      byClient: (clientId: number) => `/admin/collection-activities/${clientId}`,
    },
    // NAT-2523 — the retention work-list. Scope (admin: everyone; agent: their own book) is
    // enforced by the backend in the query, never by a parameter sent from here.
    retention: {
      root: '/admin/retention',
      owners: '/admin/retention/owners',
      entry: (clientId: number) => `/admin/retention/${clientId}`,
    },
    // NAT-2559 — Scouting: venues found by Natives Scout. Admin only in 1a.
    venues: {
      root: '/admin/venues',
      entry: (id: string) => `/admin/venues/${id}`,
      followup: (id: string) => `/admin/venues/${id}/followup`,
      attempts: (id: string) => `/admin/venues/${id}/attempts`,
      decision: (id: string, candidateId: string) =>
        `/admin/venues/${id}/candidates/${candidateId}/decision`,
      exclusion: (id: string) => `/admin/venues/${id}/exclusion`,
      doNotContact: (id: string) => `/admin/venues/${id}/do-not-contact`,
      unlink: (id: string) => `/admin/venues/${id}/unlink`,
      byClient: (slug: string) => `/admin/venues/by-client/${encodeURIComponent(slug)}`,
    },
    // NAT-2562 — the notifications box on Operativa. Admin only: the backend answers agents 403.
    notifications: {
      root: '/admin/notifications',
      acknowledge: (id: string) => `/admin/notifications/${id}/acknowledge`,
    },
    // NAT-2610 — the Situazione detail pages: today plus the monthly history. Admin only.
    positions: {
      receivables: '/admin/positions/receivables',
      // NAT-2612 — «Magazzino fermo».
      stock: '/admin/positions/stock',
      slowMovers: '/admin/positions/stock/slow-movers',
    },
    // NAT-2864 — «Qualità stock». Admin only.
    stockQuality: {
      summary: '/admin/stock-quality/summary',
      wines: '/admin/stock-quality/wines',
      producers: '/admin/stock-quality/producers',
    },
    catalogPdf: {
      generate: '/admin/catalog/pdf/generate',
      status: (jobId: string) => `/admin/catalog/pdf/status/${jobId}`,
      jobs: '/admin/catalog/pdf/jobs',
    },
    // NAT-2579 — d22 «Statistiche vendite», on the sales engine. Admin only.
    salesStats: {
      query: '/admin/sales-stats/query',
      cards: '/admin/sales-stats/cards',
      filterOptions: '/admin/sales-stats/filter-options',
      customerMovement: '/admin/sales-stats/customer-movement',
      // NAT-2760 — d15/d17 slice 3: the sale lines and producer-order lines of one wine.
      orderLines: '/admin/sales-stats/order-lines',
      signals: '/admin/sales-stats/signals',
    },
    overview: {
      logistics: '/admin/dashboard/logistica-orders',
      administration: '/admin/dashboard/administration',
      currentMonthStats: '/admin/dashboard/current-month-statistics',
      currentQuarterStats: '/admin/dashboard/current-quarter-statistics',
      logisticsCsv: '/admin/logistics/courier/csv/download',
      dachserFercamCsv: '/admin/logistics/dachser-fercam/csv/download',
      logisticsCsvEmail: '/admin/logistics/couriers/reports/delivery-email-csv',
      exciseReportEmail: '/admin/excise-report/email',
      target: '/admin/dashboard/target',
      optimizationTarget: '/admin/dashboard/optimization-target',
      yearlyTrends: '/admin/dashboard/financial-yearly-trends',
      financials: '/admin/dashboard/financials',
      incomingOutgoingCashflow: '/admin/dashboard/incoming-outgoing-cashflow',
      weeklySalesPerformance: '/admin/dashboard/weekly-sales-performance',
      getKpiSummary: '/admin/dashboard/daily-kpi-summary',
      agingReceivables: '/admin/dashboard/aging/receivables',
      agingPayables: '/admin/dashboard/aging/payables',
    },
    orders: {
      client_orders: '/admin/client_orders',
      notifyStorageAboutClientOrder: (order_code: string) =>
        `/admin/client_orders/${order_code}/notify_storage`,
      notifyLogisticaAboutClientOrder: (order_code: string) =>
        `/admin/logistica-it/notify-client-order/${order_code}`,
      proFormaClientOrder: (order_code: string) => `/admin/client_orders/${order_code}/proforma`,
      clientOrderFlags: (order_code: string) => `/admin/client_orders/${order_code}/flags`,
      producer_orders: '/admin/producer_orders',
      updateClientOrderProduct: (order_code: string, id: string) =>
        `/admin/client_orders/${order_code}/client_order_products/${id}`,
      createClientOrderProduct: (order_code: string) =>
        `/admin/client_orders/${order_code}/client_order_products`,
      notifyStorageAboutProducerOrder: (order_code: string) =>
        `/admin/producer_orders/${order_code}/notify_storage`,
      notifyLogisticaAboutProducerOrder: (order_code: string) =>
        `/admin/logistica-it/notify-supplier-order/${order_code}`,
      addAvailability: (order_code: string) =>
        `/admin/producer_orders/${order_code}/add-availability`,
      producer_order_payments: `/admin/producer_order_payments`,
      payment_link: '/admin/payment_link',
      client_order_discount: '/admin/client_order_discount',
    },
    reconciliation: {
      queue: '/admin/reconciliation/queue',
      ops: '/admin/reconciliation/ops',
      wire: (id: string) => `/admin/reconciliation/wires/${id}`,
      reverse: (id: string) => `/admin/reconciliation/wires/${id}/reverse`,
      dismissals: '/admin/reconciliation/dismissals',
      openPool: '/admin/reconciliation/open-pool',
      apply: (id: string) => `/admin/reconciliation/wires/${id}/apply`,
      attribute: (id: string) => `/admin/reconciliation/wires/${id}/attribute`,
      partialInvoices: '/admin/reconciliation/partial-invoices',
      customerBalances: '/admin/reconciliation/customer-balances',
      activity: '/admin/reconciliation/activity',
      pending: '/admin/reconciliation/pending',
    },
    orderPdf: '/admin/pdf/order',
    courtesyInvoicePdf: '/admin/pdf/courtesy-invoice',
    courier: '/admin/courier',
    products: {
      root: '/admin/products',
      checkSku: '/admin/products/check-sku',
      allProducts: '/admin/products',
      pdf: '/admin/products/pdf',
      reservation: (sku: string) => `/admin/products/${sku}/reservations`,
      reservations: (sku: string, client_slug: string) =>
        `/admin/products/${sku}/reservations/${client_slug}`,
      grapeTypes: '/admin/products/grape_types',
      producerCountries: '/admin/products/filter-options/producer-countries',
      discount: (sku: string) => `/admin/products/${sku}/discounts`,
      deleteDiscounts: (id: string, product_id: string) =>
        `/admin/products/${product_id}/discounts/${id}`,
    },
    wineTypes: '/admin/wine_types',
    wineListing: '/admin/wine_listing_data',
    wineCategories: '/admin/wine_categories',
    grapeTypes: '/admin/grape_types',
    territories: '/admin/territories',
    producers: '/admin/producers',
    producer: '/admin/producer',
    producerRegions: '/admin/producer_regions',
    producerRegionsProvinces: '/admin/producer_all_region_provinces',
    productSelections: {
      root: '/admin/product-selections',
      byId: (id: string | number) => `/admin/product-selections/${id}`,
      items: (id: string | number) => `/admin/product-selections/${id}/items`,
    },
    users: '/admin/users',
    // NAT-2499 (phase 6) — a person's current grants, seeding the admin link-management
    // form's multi-select. Includes grants to inactive venues.
    userGrants: (slug: string) => `/admin/users/${slug}/grants`,
    userOrder: `/admin/users/orders`,
    producer_payments: '/admin/producer_payments',
    deleteProducerPayment: (paymentId: string) => `/admin/producer_payments/${paymentId}`,
    statistics: {
      root: '/admin/stats',
      annualStats: {
        yoy: '/admin/stats/yoy',
      },
      getOrderPlacedByStats: '/admin/stats/order-stats',
      productStats: '/admin/stats/product_stats',
      updateLogisticsCost: '/admin/stats/update_logistics_cost',
      deleteLogisticsCost: (id: string) => `/admin/logistics/details/${id}`,
      logisticsTypeValues: '/admin/stats/get_logistics_type',
      logisticsSupplierList: '/admin/stats/logistics_supplier_list',
      profitability: 'admin/stats/profitability',
      stock_optimization: 'admin/stats/stock-optimization',
      price_range_stock_behaviour: 'admin/stats/price-range-stock-behaviour',
      single_range_stock_behaviour: 'admin/stats/single-range-stock-behaviour',
      yearly_sales: 'admin/stats/yearly-sales',
      sales_flow: 'admin/stats/sales-flow',
      product_performance: '/admin/product-performance',
      analytics: {
        priceDistribution: '/admin/analytics/price-distribution',
        wineDistribution: '/admin/analytics/wine-distribution',
      },
      producerRisk: '/admin/stats/producer-risk',
    },
    reservations: '/admin/reservations',
    discounts: '/admin/discounts',
    tastings: '/admin/tastings',
    createClient: (slug: string) => `/admin/tastings/${slug}/tasting-clients`,
    deleteClient: (params: { slug: string; clientSlug: string }) =>
      `/admin/tastings/${params.slug}/tasting-clients/${params.clientSlug}`,
    createProduct: (slug: string) => `/admin/tastings/${slug}/tasting-products`,
    deleteProduct: (params: { slug: string; sku: string }) =>
      `/admin/tastings/${params.slug}/tasting-products/${params.sku}`,
  },
  customer: {
    payment_status: '/customer/payment-status',
    selections: '/customer/selections',
    per_te: '/customer/per-te',
    producers: { root: '/customer/producers', countries: '/customer/producers/countries' },
    products: {
      root: '/customer/products',
      volumes: '/customer/products/volumes',
      grape_types: '/customer/products/grape_types',
      wine_types: '/customer/products/wine_types',
      pricing: '/customer/products/pricing',
    },
    client_orders: {
      root: '/customer/client_orders',
      current: '/customer/client_orders/current',
      current_order: '/customer/client_orders/currentOrder',
      details: (order_code: string) => `/customer/client_orders/${order_code}/new`,
    },
    overlay: {
      active: '/customer/overlay/active',
      fallback: '/customer/overlay/fallback',
      mark_seen: '/customer/overlay',
      client_details: '/customer/client-details',
    },
    cart_products: {
      root: '/customer/cart_products',
      checkout: '/customer/checkout',
    },
    clients: '/customer/clients',
    my_clients: '/customer/my-clients',
    client_settings: '/customer/client-settings',
  },
  utils: {
    files: {
      link: '/utils/files/link/create',
    },
    clientCategories: '/utils/client-categories',
    userRoles: '/utils/user-roles',
    promotionTypes: '/utils/promotion-types',
  },
  public: {
    clientPdfDownload: (token: string) => `public/products/pdf/${token}/download`,
    generic: `public/products/pdf/download/generic`,
    exciseRegister: (date: string) => `public/excise-register?date=${date}`,
  },
};

export const endpointsLegacy = {
  auth: {
    login: '/auth/login',
    refreshToken: '/auth/refresh-token',
  },
  admin: {
    clients: '/admin/clients',
    agents: '/admin/agents',
    clientRegions: '/admin/client_regions',
    orders: {
      client_orders: '/admin/client_orders',
      producer_orders: '/admin/producer_orders',
    },
    products: {
      root: '/admin/products',
      allProducts: '/admin/products',
      producers: '/admin/producers',
      reservation: (sku: string) => `admin/products/${sku}/reservations`,
      reservations: (sku: string, client_slug: string) =>
        `admin/products/${sku}/reservations/${client_slug}`,
      grapeTypes: '/admin/products/grape_types',
      discount: (sku: string) => `admin/products/${sku}/discounts`,
      deleteDiscounts: (id: string, product_id: string) =>
        `admin/products/${product_id}/discounts/${id}`,
    },
    producers: '/admin/producers',
    users: '/admin/users',
    statistics: {
      root: '/admin/stats',
      saleStats: '/admin/sale-stats',
      bestSellingProductsStats: '/admin/best-selling-sale-stats',
      annualStats: {
        yoy: '/admin/stats/yoy',
      },
      annualStat: '/admin/stats/annual-stat',
      wineType: '/admin/stats/wine-type',
      getYoyStats: '/admin/stats/get-yoy-stat',
      getOrderPlacedByStats: '/admin/stats/order-stats',
      productStats: '/admin/product_stats',
      updateLogisticsCost: '/admin/stats/update_logistics_cost',
      profitability: 'admin/stats/profitability',
    },
    reservations: '/admin/reservations',
    tastings: '/admin/tastings',
  },
  customer: {
    producers: { root: '/customer/producers', countries: '/customer/producers/countries' },
    products: {
      root: '/customer/products',
      volumes: '/customer/products/volumes',
      grape_types: '/customer/products/grape_types',
      wine_types: '/customer/products/wine_types',
    },
    client_orders: {
      root: '/customer/client_orders',
      current: '/customer/client_orders/current',
      current_order: '/customer/client_orders/currentOrder',
    },
    cart_products: {
      root: '/customer/cart_products',
    },
  },
};

// ----------------------------------------------------------------------

/**
 * NAT-2804: set once this document's session is ending — a logout, or a refresh that failed
 * closed. The document is about to be replaced by a navigation to the login page, so it is never
 * reset: until then no request leaves with any token, and no refresh reads or writes storage,
 * whatever another tab stores in the meantime.
 */
let documentSessionEnded = false;

export function endDocumentSession() {
  documentSessionEnded = true;
}

export function isDocumentSessionEnded() {
  return documentSessionEnded;
}

/**
 * NAT-2828: a value every login write and every session clear changes, in any tab. It is never
 * removed, so a login and a logout elsewhere cannot leave storage looking as it did before them.
 * A login pending in this tab reads it before signing in and writes nothing if it moved.
 */
export const SESSION_REVISION_KEY = 'sessionRevision';

export function bumpSessionRevision() {
  localStorageSetItem(
    SESSION_REVISION_KEY,
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
  );
}

export function readSessionRevision() {
  return localStorageGetItem(SESSION_REVISION_KEY);
}

/** Tests only: the flag is module state, shared by every test in a file. A no-op in the app. */
export function resetDocumentSessionForTests() {
  if (import.meta.env.MODE !== 'test') return;
  documentSessionEnded = false;
}

/**
 * NAT-2804: whether the stored user record is the person Firebase holds: the stored id is the
 * Firebase uid, or both carry an email and the emails are equal, case-insensitively. Login finds
 * the record by the token's email, and the id alone cannot decide a mismatch: sessions stored
 * before NAT-2165 carry no id, and users.id can drift from the Firebase uid after a restore.
 * Anything else — a record that cannot be read, is not an object, or names no one on either
 * side — is no match. NAT-2828: the assistant's bearer asks the same question.
 */
export function isSameFirebaseUser(storedUserRaw: string, firebaseUser: User): boolean {
  let stored: unknown;
  try {
    stored = JSON.parse(storedUserRaw);
  } catch {
    return false;
  }
  if (typeof stored !== 'object' || stored === null) return false;
  const { id, email } = stored as { id?: unknown; email?: unknown };
  if ((typeof id === 'number' || typeof id === 'string') && String(id) === firebaseUser.uid) {
    return true;
  }
  if (typeof email !== 'string' || !email || !firebaseUser.email) return false;
  return email.toLowerCase() === firebaseUser.email.toLowerCase();
}

const createAxiosInstance = ({
  baseURL,
  isLegacyURL = false,
}: {
  baseURL: string;
  isLegacyURL?: boolean;
}) => {
  const tokenKey = isLegacyURL
    ? AUTH_STORAGE_KEYS.accessTokenLegacy
    : AUTH_STORAGE_KEYS.accessToken;

  const customAxiosInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const firebaseApp = initializeApp(FIREBASE_API);

  const AUTH = getAuth(firebaseApp);

  customAxiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // NAT-2804: nothing leaves a document whose session is ending, whatever token is stored now.
      if (documentSessionEnded) throw new CanceledError('The session of this page has ended');
      const token = localStorageGetItem(tokenKey);

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  const fetchCurrentUser = () =>
    new Promise<User | null>((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        AUTH,
        (user) => {
          unsubscribe();
          resolve(user);
        },
        reject,
      );
    });

  // NAT-2804: a refresh writes tokens only for the session that is stored when Firebase answers.
  // With no stored user there is no session to refresh — a public page (whose pdfToken sits in the
  // access token slot) or a logout that cleared storage while Firebase was answering — and the
  // refresh rejects without touching storage. A Firebase user who is not the stored one is never
  // written over it: that fails closed, as any other refresh failure does.
  // Once this document's session is ending the refresh neither reads nor writes storage — another
  // tab may have stored the next person's session there — and nothing is retried.
  const refreshAuthToken = async (failedRequest: unknown) => {
    if (documentSessionEnded) return Promise.reject(failedRequest);
    if (!localStorageGetItem(AUTH_STORAGE_KEYS.user)) return Promise.reject(failedRequest);
    try {
      const currentUser = await fetchCurrentUser();
      if (documentSessionEnded) return await Promise.reject(failedRequest);
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      const accessToken = await currentUser.getIdToken(true);
      if (documentSessionEnded) return await Promise.reject(failedRequest);

      // A stored user removed meanwhile is no match, and the catch below rejects without a trace.
      if (!isSameFirebaseUser(localStorageGetItem(AUTH_STORAGE_KEYS.user) ?? '', currentUser)) {
        throw new Error('The Firebase user is not the stored user');
      }
      localStorageSetItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
      localStorageSetItem(AUTH_STORAGE_KEYS.refreshToken, currentUser.refreshToken);

      return await Promise.resolve();
    } catch (error) {
      if (documentSessionEnded) return Promise.reject(error);
      if (!localStorageGetItem(AUTH_STORAGE_KEYS.user)) return Promise.reject(error);
      // Fails closed as a logout does: the session ends here, storage is cleared, Firebase is signed
      // out best-effort and the document is replaced by the login page without waiting for it.
      // NAT-2860: the revision moves before the first key goes, as a logout's does.
      endDocumentSession();
      bumpSessionRevision();
      Object.values(AUTH_STORAGE_KEYS).forEach((key) => {
        localStorageRemoveItem(key);
      });
      clearActingClientStorage();
      signOut(AUTH).catch(() => undefined);
      window.location.replace(paths.login);
      return Promise.reject(error);
    }
  };

  // NAT-2494: a 403 is a REFUSAL, not an expired session. The API answers 401
  // (`ERR_INVALID_TOKEN`, `forbiddenFirebase`) when the Firebase token is
  // expired or invalid, and 403 when the caller is authenticated and not
  // allowed — wrong role for the route, or acting for a client that is not
  // theirs (NAT-2335). Registering 403 here read every refusal as a token
  // problem: refresh, retry, get the same 403, and on any hiccup in the refresh
  // clear every auth key and bounce to login. Once a person can be linked to
  // several venues, "not allowed for this venue" has to be a normal answer.
  //
  // One exception, kept on purpose: a request that went out with NO token at
  // all also gets a 403 from the API (`auth.middleware.ts` answers a missing
  // header with `forbidden`, not 401 — NAT-2508 records the inconsistency).
  // That state is reachable with a live Firebase session — the public PDF page
  // writes '' over the stored token when opened without `?data=` — and before
  // this change the refresh healed it silently. So: refresh on 401, and on a
  // 403 only when we know we sent no credential. A 403 WITH a token is final.
  // NAT-2823: a request marked skipAuthRefresh (the login request) is never refreshed and retried;
  // axios-auth-refresh drops it before shouldRefresh is asked.
  createAuthRefreshInterceptor(customAxiosInstance, refreshAuthToken, {
    shouldRefresh: (error) => {
      const status = error?.response?.status;
      if (status === 401) return true;
      return status === 403 && !localStorageGetItem(AUTH_STORAGE_KEYS.accessToken);
    },
  });

  // NAT-2497: a 403 that carried a token is an authorization refusal, and a
  // grant can be revoked mid-session. The acting-client provider listens for
  // this, reloads the grants and drops a selection they no longer contain, so
  // the app re-gates instead of retrying a venue that was taken away.
  customAxiosInstance.interceptors.response.use(
    (res) => res,
    (error) => {
      if (
        isActingClientRefusal(
          error?.response?.status,
          localStorageGetItem(AUTH_STORAGE_KEYS.accessToken),
          error?.config?.url,
          endpoints.customer.my_clients,
        )
      ) {
        signalActingClientRefused();
      }
      return Promise.reject(error);
    },
  );

  return customAxiosInstance;
};

export const axiosInstanceLegacy = createAxiosInstance({
  baseURL: HOST_API_LEGACY as string,
  isLegacyURL: false,
});
const axiosInstance = createAxiosInstance({ baseURL: HOST_API as string });
export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcherLegacy = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const { data } = await axiosInstanceLegacy.get(url, { ...config });

  return data;
};

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const {
    data: { data },
  } = await axiosInstance.get(url, { ...config });

  return data;
};
